<template>
  <div>
    <BaseSwitch
      :value="internalValue"
      :disabled="isDisabled || isToggling"
      @toggle="onToggle"
    />
    <BDXManagementTableFCPToggleStartDateModal
      v-if="!isDisabled"
      v-model="showFCPStartDateModal"
      :id="$props.id"
      :date="fcpStartDate"
      :account-type="accountType"
      :account-name="accountName"
      :umr="umr"
      :is-turning-on="isTurningOn"
      @save="handleSave"
      @cancel="handleCancel"
    />
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch } from '@vue/composition-api'
import { LincolnGlobalUMRService } from '@services'
import { useCurrentUser, useUserCompany, useTryCatch } from '@composables'
import { BaseSwitch } from '@components/base'
import BDXManagementTableFCPToggleStartDateModal from './BDXManagementTableFCPToggleStartDateModal.vue'
import { clearAccountNameCache } from '../bdx-edit-form/BDXEditFormAccountName.vue'

export default defineComponent({
  name: 'BDXManagementTableFCPToggle',
  components: {
    BaseSwitch,
    BDXManagementTableFCPToggleStartDateModal
  },
  props: {
    value: Boolean,
    id: Number,
    startDate: String,
    accountType: String,
    accountName: String,
    // Key's UMR - scopes the Account Name dropdown in the FCP details modal (LIQ-2410)
    umr: String,
    // This key's CURRENT rec_status (item.rec_status) - only matters for the superuser "turning
    // FCP off leaves an already-ON REC alone" rule below (resolveRecStatus); a non-superuser's
    // own FCP-off rule always forces REC off regardless of its current value, so recStatus is
    // never even read for them.
    recStatus: Boolean
  },
  emits: ['saved:fcp-start-date'],
  setup(props, { emit }) {
    const { userEmail, isSuperUser } = useCurrentUser()
    const { isRoleName: isCompanyRoleName } = useUserCompany()

    // A non-superuser has no direct access to the REC STATUS column/toggle at all (see
    // BDXManagementTable.vue's own v-if="isSuperUser") - turning FCP on/off is the only lever
    // they have. A superuser DOES have direct access (see GlobalUMRStatusToggle's own
    // isDisabled). Turning FCP ON turns REC on unconditionally for both roles, but turning FCP
    // OFF now diverges again per explicit decision:
    //   - Superuser: REC is only forced off if it wasn't already ON - an already-ON REC is left
    //     untouched, so turning FCP off never silently undoes a REC the superuser directly turned
    //     on themselves via the separate REC STATUS toggle.
    //   - Non-superuser: REC always mirrors FCP - turning FCP off always forces REC off too, no
    //     exception. A non-superuser has no separate REC toggle to have deliberately turned it on
    //     with in the first place, so there's nothing for this to risk undoing.
    //
    // Neither direction is gated on any associated company having rec_enabled=1 (that check -
    // GlobalUMRStatusToggle's own hasQualifyingCompany - was removed entirely per explicit
    // decision; a superuser's direct REC toggle is now blocked only by FCP being on, same as
    // this cascade's own reasoning above).
    const resolveRecStatus = (turningOn) => {
      if (!turningOn) {
        if (isSuperUser.value && props.recStatus) return undefined
        return false
      }

      return true
    }

    const fcpStartDate = ref(props.startDate)
    const showFCPStartDateModal = ref(false)
    const isTurningOn = ref(false)

    const internalValue = ref(props.value)

    const isDisabled = computed(() => {
      if (isSuperUser.value) return false
      return !isCompanyRoleName('TPA') && !isCompanyRoleName('Underwriter')
    })

    const isToggling = ref(false)

    watch(
      () => props.value,
      (newValue) => {
        internalValue.value = newValue
      }
    )

    const onToggle = async (status) => {
      if (status) {
        internalValue.value = true
        isTurningOn.value = true
        showFCPStartDateModal.value = true
      } else {
        internalValue.value = false
        isTurningOn.value = false
        isToggling.value = true

        await useTryCatch(async () => {
          const recStatus = resolveRecStatus(false)

          await LincolnGlobalUMRService.updateStatus(userEmail.value, [
            {
              id: props.id,
              fcp_status: false,
              fcp_start_date: null,
              account_type: null,
              account_name: null,
              ...(undefined !== recStatus ? { rec_status: recStatus } : {})
            }
          ])

          emit('saved:fcp-start-date', true)
        })

        isToggling.value = false
      }
    }

    const handleSave = async (data) => {
      isToggling.value = true

      await useTryCatch(async () => {
        const recStatus = resolveRecStatus(true)

        await LincolnGlobalUMRService.updateStatus(userEmail.value, [
          {
            id: props.id,
            fcp_status: true,
            fcp_start_date: data.date,
            account_type: data.accountType,
            account_name: data.accountName,
            ...(undefined !== recStatus ? { rec_status: recStatus } : {})
          }
        ])

        // A newly typed Account Name is now on the key; refetch so it shows in later dropdowns.
        clearAccountNameCache(props.umr)

        emit('saved:fcp-start-date', true)
        showFCPStartDateModal.value = false
      })

      isToggling.value = false
      isTurningOn.value = false
    }

    const handleCancel = () => {
      if (isTurningOn.value) {
        internalValue.value = false
        showFCPStartDateModal.value = false
        isTurningOn.value = false
      }
    }

    return {
      fcpStartDate,
      showFCPStartDateModal,
      isTurningOn,
      internalValue,
      isDisabled,
      isToggling,
      onToggle,
      handleSave,
      handleCancel
    }
  }
})
</script>
